import { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import TablePagination from "@mui/material/TablePagination";

import { request } from "@/Api";
import CsvExportButton from "@/common/CsvExportButton";
import CustomFilters from "@/table/CustomFilters";
import CustomTable from "@/table/CustomTable";
import { getDataFromQueryParams } from "@/utils";

export default function CustomTableContainer({
    modelType,
    defaultOrder,
    title,
    headCells,
    itemRowComponent,
    itemRowProps,
    filterComponents,
    setAdditionalData,
    connectedComponents,
    children,
    prefixedFilters,
    customBottomLeftComponent,
    onLoaded,
    onFilterChange,
    height,
}) {
    const ItemRow = itemRowComponent;
    const defaultParams = {
        page: 0,
        limit: 100,
        orders: defaultOrder,
        filters: {},
    };
    const CustomBottomLeftComponent = customBottomLeftComponent;

    const [order, setOrder] = useState(defaultParams.orders);
    const [page, setPage] = useState(defaultParams.page);
    const [rowsPerPage, setRowsPerPage] = useState(defaultParams.limit);
    const [filters, setFilters] = useState(defaultParams.filters);
    const [activeCustomFilter, setActiveCustomFilter] = useState(null);
    const [customFilters, setCustomFilters] = useState([]);
    const [isReady, setIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [items, setItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    const getCustomFilters = () => {
        request.get(`custom_filters`).then(function (response) {
            const filteredFilters = response.data.filter(
                (filter) => filter.model_type == modelType,
            );
            setCustomFilters([...filteredFilters]);

            const parsedQueryParams = getDataFromQueryParams(defaultParams);
            const queryParamFilterString = JSON.stringify(
                parsedQueryParams.filters,
            );
            const defaultCustomFilter = filteredFilters.find(
                (filter) => filter.is_default,
            );
            const matchingFilter = filteredFilters.find(
                (filter) => filter.filters == queryParamFilterString,
            );

            if (defaultCustomFilter && parsedQueryParams.allDefault) {
                setActiveCustomFilter(defaultCustomFilter);
                setFilters(JSON.parse(defaultCustomFilter.filters));
                setOrder(defaultCustomFilter.orders);
            } else if (
                matchingFilter &&
                matchingFilter.filters == queryParamFilterString
            ) {
                setActiveCustomFilter(matchingFilter);
                setFilters(JSON.parse(matchingFilter.filters));
                setOrder(matchingFilter.orders);
            } else {
                setFilters(parsedQueryParams.filters);
                setOrder(parsedQueryParams.orders);
            }

            setPage(parsedQueryParams.page);
            setRowsPerPage(parsedQueryParams.limit);
            setIsReady(true);
        });
    };

    const getItems = () => {
        if (!isReady) {
            return;
        }

        request
            .get(`/${modelType}s`, {
                params: {
                    offset: page * rowsPerPage,
                    limit: rowsPerPage,
                    orders: order,
                    ...filters,
                    ...prefixedFilters,
                },
            })
            .then((response) => {
                setTotalCount(response.data.total_count);
                setItems(response.data.items);
                onLoaded && onLoaded(response.data.items);
                setIsLoading(false);

                if (setAdditionalData) {
                    setAdditionalData(response.data);
                }

                const query_params = response.request.responseURL.split("?")[1];
                history.replaceState(
                    null,
                    "",
                    `${location.pathname}?${query_params}`,
                );
            });
    };

    useEffect(getCustomFilters, []);

    useEffect(() => {
        if (!isReady) {
            return;
        }
        const debounceTimeout = setTimeout(() => {
            getItems();
        }, 250);
        return () => clearTimeout(debounceTimeout);
    }, [order, page, rowsPerPage, filters, isReady]);

    useEffect(() => {
        onFilterChange && onFilterChange(getItems);
        if (page > 0) {
            setPage(0);
        }
    }, [filters]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const addItem = (item) => {
        setItems([item, ...items]);
    };

    const addItems = (newItems) => {
        setItems([...newItems, ...items]);
    };

    const updateItem = (id, itemData) => {
        setItems(items.map((item) => (item.id == id ? itemData : item)));
    };

    const deleteItem = (itemId) => {
        setItems(items.filter((item) => item.id != itemId));
    };

    return (
        <>
            {prefixedFilters ? null : (
                <CustomFilters
                    title={title}
                    modelType={modelType}
                    filterComponents={filterComponents}
                    filters={filters}
                    setFilters={setFilters}
                    order={order}
                    setOrder={setOrder}
                    defaultOrder={defaultOrder}
                    customFilters={customFilters}
                    setCustomFilters={setCustomFilters}
                    activeCustomFilter={activeCustomFilter}
                    setActiveCustomFilter={setActiveCustomFilter}
                />
            )}

            {children}
            {connectedComponents ? connectedComponents(items, setItems) : null}

            <CustomTable
                isLoading={isLoading}
                headCells={headCells}
                setOrder={setOrder}
                order={order.substring(1)}
                sign={order.substring(0, 1)}
                rows={items}
                rowComponent={(item) => (
                    <ItemRow
                        item={item}
                        items={items}
                        addItem={addItem}
                        updateItem={updateItem}
                        setItems={setItems}
                        deleteItem={deleteItem}
                        {...itemRowProps}
                    />
                )}
                height={height}
            />

            {prefixedFilters ? null : (
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Grid item xs={12} sm={6}>
                        {CustomBottomLeftComponent ? (
                            <CustomBottomLeftComponent
                                addItem={addItem}
                                addItems={addItems}
                                updateItem={updateItem}
                                getItems={getItems}
                            />
                        ) : (
                            <CsvExportButton
                                model={modelType}
                                filters={filters}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TablePagination
                            rowsPerPageOptions={[10, 50, 100]}
                            component="div"
                            count={totalCount}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Grid>
                </Grid>
            )}
        </>
    );
}
